export interface UiStorageScope {
  key: string;
  installationKey: string;
  userId: string;
  projectId: string;
}

interface UiStorageScopeParams {
  userId?: string | null;
  projectId?: string | null;
}

const UNKNOWN_INSTALLATION_KEY = "unknown-origin";

// Where an account signed in without a project of its own is filed. An
// administrator holding `*.*.*` reads across every project and its token
// carries none, so a scope built from the project alone would be no scope at
// all -- and a sidebar that is never synced is an empty sidebar. Such a session
// is stored under this id instead, beside the account's own project scope.
export const NO_PROJECT_SCOPE_ID = "no-project";

export function createUiStorageScope(
  params: UiStorageScopeParams,
): UiStorageScope | null {
  const userId = params.userId?.trim();
  if (!userId) return null;

  const projectId = params.projectId?.trim() || NO_PROJECT_SCOPE_ID;

  const installationKey = getInstallationKey();

  return {
    key: [
      installationKey,
      userId,
      projectId,
    ].map(encodeURIComponent).join(":"),
    installationKey,
    userId,
    projectId,
  };
}

// Where the pages a visitor reads without signing in are stored. They belong to
// no account -- an installation answers the same public pages to everyone -- so
// they are filed under the installation alone, and a session ending never takes
// them with it.
export const PUBLIC_SCOPE_ID = "public";

export function createPublicUiStorageScope(): UiStorageScope {
  // Built the way every other scope is, from the id that stands in for the
  // account, so a reader naming that id lands in the very same scope. Both
  // parts are given, so the scope is never the null an unknown session gets.
  return createUiStorageScope({
    projectId: PUBLIC_SCOPE_ID,
    userId: PUBLIC_SCOPE_ID,
  }) as UiStorageScope;
}

function getInstallationKey(): string {
  if (typeof window === "undefined") return UNKNOWN_INSTALLATION_KEY;

  return window.location.origin || UNKNOWN_INSTALLATION_KEY;
}
